import React, { useEffect, useState } from 'react';

// Lazy-load Leaflet to avoid SSR issues and keep bundle split
let LeafletLoaded = false;
let MapContainer, TileLayer, Marker, Popup, Polyline, L;

async function loadLeaflet() {
  if (LeafletLoaded) return true;
  try {
    const leaflet = await import('leaflet');
    const reactLeaflet = await import('react-leaflet');
    L = leaflet.default;
    MapContainer = reactLeaflet.MapContainer;
    TileLayer = reactLeaflet.TileLayer;
    Marker = reactLeaflet.Marker;
    Popup = reactLeaflet.Popup;
    Polyline = reactLeaflet.Polyline;

    // Fix Leaflet default icon paths (CRA issue)
    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
      iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
      shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
    });

    // Inject Leaflet CSS
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }

    LeafletLoaded = true;
    return true;
  } catch (err) {
    console.warn('Leaflet non disponible:', err.message);
    return false;
  }
}

// Custom colored icon for checkpoints
function createIcon(color, type) {
  if (!L) return null;
  const emoji = type === 'start' ? '🟢' : type === 'finish' ? '🔴' : '📍';
  return L.divIcon({
    html: `<div style="
      width:32px;height:32px;border-radius:50%;
      background:${color};border:3px solid #fff;
      box-shadow:0 2px 8px rgba(0,0,0,0.3);
      display:flex;align-items:center;justify-content:center;
      font-size:14px;
    ">${type === 'start' ? 'S' : type === 'finish' ? 'F' : '●'}</div>`,
    className: '',
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -18]
  });
}

export default function InteractiveMap({ territory }) {
  const [leafletReady, setLeafletReady] = useState(LeafletLoaded);
  const [MapComp, setMapComp] = useState(null);

  useEffect(() => {
    if (!leafletReady) {
      loadLeaflet().then(ok => {
        if (ok) {
          setLeafletReady(true);
          // Force re-render with the loaded components
          setMapComp(() => MapContainer);
        }
      });
    } else {
      setMapComp(() => MapContainer);
    }
  }, []);

  const checkpoints = territory.checkpoints || [];
  const center = territory.mapCenter
    ? [territory.mapCenter.lat || territory.mapCenter[0], territory.mapCenter.lng || territory.mapCenter[1]]
    : [48.0, 6.9];
  const zoom = territory.mapZoom || 12;
  const polylinePoints = checkpoints
    .sort((a, b) => a.order - b.order)
    .map(cp => [cp.lat, cp.lng]);

  if (!leafletReady || !MapComp) {
    return (
      <div style={{
        background: 'linear-gradient(135deg, #E8EFE4, #D4E8D0)',
        borderRadius: 4, minHeight: 380, display: 'flex',
        flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        border: '1px solid rgba(140,122,106,0.2)', marginBottom: 32
      }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🗺️</div>
        <p style={{ color: '#5A4A3C', fontWeight: 500 }}>Chargement de la carte...</p>
        <p style={{ fontSize: 12, color: '#8C7A6A', marginTop: 4 }}>
          Installez react-leaflet : npm install leaflet react-leaflet
        </p>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 32 }}>
      <MapComp
        center={center}
        zoom={zoom}
        style={{ height: 420, borderRadius: 4, border: '1px solid rgba(140,122,106,0.2)' }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Route line */}
        {polylinePoints.length > 1 && (
          <Polyline
            positions={polylinePoints}
            pathOptions={{ color: territory.color || '#E8532A', weight: 3, opacity: 0.8, dashArray: null }}
          />
        )}

        {/* Checkpoints */}
        {checkpoints.map((cp, i) => (
          <Marker
            key={cp._id || i}
            position={[cp.lat, cp.lng]}
            icon={createIcon(
              cp.type === 'start' ? '#2E7D32' : cp.type === 'finish' ? '#E8532A' : (territory.color || '#E8532A'),
              cp.type
            )}
          >
            <Popup>
              <div style={{ fontFamily: 'sans-serif', minWidth: 160 }}>
                <strong style={{ display: 'block', marginBottom: 4 }}>{cp.name}</strong>
                <span style={{
                  fontSize: 11, padding: '2px 8px', borderRadius: 2,
                  background: cp.type === 'start' ? '#E8F5E9' : cp.type === 'finish' ? '#FBE9E7' : '#F5F0E8',
                  color: cp.type === 'start' ? '#2E7D32' : cp.type === 'finish' ? '#E8532A' : '#8C7A6A'
                }}>
                  {cp.type === 'start' ? 'Départ' : cp.type === 'finish' ? 'Arrivée' : `Checkpoint ${cp.order}`}
                </span>
                <div style={{ fontSize: 11, color: '#8C7A6A', marginTop: 6 }}>
                  {cp.lat.toFixed(5)}°N · {cp.lng.toFixed(5)}°E
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapComp>
    </div>
  );
}
